import React, { useEffect, useState } from 'react';
import { Button } from './ui/Button';
import { CalButton } from './ui/CalButton';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { heroCopy } from '../data/translations/hero';
import { useScreenInit } from '../useScreenInit.js';
import { HERO_PHOTO_FILTER as WARM_FILTER } from '../utils/photoFilters';
const MAIN_IMAGE = 'https://cdn.magicpatterns.com/uploads/n3bzG31neGVxt4AvsV2ris/WhatsApp_Image_3.jpg';
const PORTRAIT_IMAGE = 'https://cdn.magicpatterns.com/uploads/iW4WwChvhBykwEnaEEiJB1/LR21.png';
const LOGO_ICON = 'https://cdn.magicpatterns.com/uploads/kWRktVv4YzBL2Ep7rCMN6b/logo-ster-transparant.png';
const SWAP = {
  duration: 0.2,
  ease: 'easeOut' as const
};
export function HeroSection() {
  const screenInit = useScreenInit();
  const [storyOpen, setStoryOpen] = useState<boolean>(screenInit?.storyOpen ?? false);
  const [canHover, setCanHover] = useState(false);
  const {
    language
  } = useLanguage();
  const copy = heroCopy[language];
  useEffect(() => {
    const query = window.matchMedia('(hover: hover) and (pointer: fine)');
    setCanHover(query.matches);
    const onChange = (event: MediaQueryListEvent) => setCanHover(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return <section className="hero-curve relative z-10 mt-[108px] md:mt-[120px] min-h-[calc(100vh-120px)] flex items-center justify-center pt-20 md:pt-[130px] pb-[100px] md:pb-[180px] bg-navy text-cream-50" data-id="element-55" data-mp="2z39cxlfdzpoh">
      {/* Soft fade above and below, following the curves */}
      <div aria-hidden="true" className="hero-curve hero-halo absolute inset-0 -z-10 pointer-events-none" data-id="element-56" data-mp="3823c9jk3qu5q"></div>
      {/* Cream fading inward along the curved edges */}
      <div aria-hidden="true" className="hero-curve hero-inner-fade absolute inset-0 z-0 pointer-events-none" data-id="element-57" data-mp="1dqesn42xe3f0"></div>
      {/* Background texture, clipped to the same curve */}
      <div className="hero-curve absolute inset-0 bg-grain opacity-20 z-0 mix-blend-overlay pointer-events-none" data-id="element-58" data-mp="38v5xzdivmcvr"></div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 w-full" data-id="element-59" data-mp="3du8yqlrtpwxx">
        {/* Top — centered logo + headline */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        ease: 'easeOut'
      }} className="text-center mb-12" data-id="element-60" data-mp="3mtm1ywpctpa7">
          <div className="flex justify-center mb-5" data-id="element-61" data-mp="l49g094dumb">
            <img src={LOGO_ICON} alt={copy.logoAlt} className="w-16 h-16" data-id="element-62" data-mp="jjfocumqfpi" />
          </div>

          <motion.h1 initial={{
          opacity: 0,
          filter: 'blur(4px)'
        }} animate={{
          opacity: 1,
          filter: 'blur(0px)'
        }} transition={{
          duration: 1.2,
          delay: 0.3,
          ease: 'easeOut'
        }} className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight text-gold-400 mb-2" data-id="element-63" data-mp="5epbq4hqxarb">
            {copy.headline}
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 0.6,
          ease: 'easeOut'
        }} style={{
          maxWidth: '560px'
        }} className="mt-4 mx-auto font-sans text-base md:text-lg leading-relaxed text-cream-50/80 font-light" data-id="element-64" data-mp="23v7lzodlj7w9">
            {copy.headlineIntro}
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 0.85,
          ease: 'easeOut'
        }} className="mt-7 flex flex-col items-center" data-id="element-65" data-mp="2mjmekg3katz6">
            <span style={{
            backgroundColor: '#D4A24C'
          }} className="block w-12 h-px mb-5" aria-hidden="true" data-id="element-66" data-mp="28tivfxzzbgdy"></span>
            <p className="font-serif italic text-lg md:text-2xl leading-snug text-cream-50 max-w-2xl" data-id="element-67" data-mp="36a7y1twr6moe">
              {copy.subheadline}
            </p>
          </motion.div>
        </motion.div>

        {/* Bottom — imagery left, story right */}
        <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-start mb-10" data-id="element-68" data-mp="2q2a39888fon0">
          {/* Left — imagery */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 1,
          delay: 0.4,
          ease: 'easeOut'
        }} className="self-start flex justify-center md:justify-start" data-id="element-69" data-mp="2c30yd4ph8gyc">
            <div className="w-full md:sticky md:top-28" data-id="element-70" data-mp="38s8j4ai9um17">
              <AnimatePresence mode="wait" initial={false} data-id="element-71" data-mp="38rqheycubflw">
                {storyOpen ? <motion.div key="portrait" initial={{
                opacity: 0,
                scale: 0.98
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.98
              }} transition={SWAP} className="w-full" data-id="element-72" data-mp="2v0bwun3ab6cq">
                    <img src={PORTRAIT_IMAGE} alt={copy.portraitAlt} style={{
                  filter: WARM_FILTER,
                  objectPosition: '50% 22%',
                  height: 'min(calc(100vh - 136px), 720px)'
                }} className="w-full rounded-2xl shadow-2xl object-cover aspect-[4/5] md:aspect-auto" data-id="element-73" data-mp="3si286gtlqn1q" />
                  </motion.div> : <motion.div key="collapsed" initial={{
                opacity: 0,
                scale: 0.98
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.98
              }} transition={SWAP} className="relative w-full pb-8" data-id="element-74" data-mp="3eulbthzpza3w">
                    <img src={MAIN_IMAGE} alt={copy.mainImageAlt} style={{
                  filter: WARM_FILTER,
                  objectPosition: '68% 50%'
                }} className="w-full rounded-2xl shadow-2xl object-cover aspect-[3/2] md:aspect-auto md:h-[420px] lg:h-[440px]" data-id="element-75" data-mp="182u9s5xovcol" />
                    <div style={{
                  borderColor: '#D4A24C'
                }} className="absolute -bottom-2 -left-3 md:-bottom-4 md:-left-10 w-[32%] aspect-[4/5] rounded-xl border-2 overflow-hidden shadow-[0_12px_32px_rgba(0,0,0,0.45)]" data-id="element-76" data-mp="1f96iued4l2us">
                      <img src={PORTRAIT_IMAGE} alt={copy.portraitAlt} style={{
                    width: '256%',
                    left: '-80%',
                    top: '-5.3%',
                    filter: WARM_FILTER
                  }} className="absolute max-w-none" data-id="element-77" data-mp="rfocgxnd2rvt" />
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </motion.div>

          {/* Right — Your Coach story */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 1,
          delay: 0.6,
          ease: 'easeOut'
        }} onMouseEnter={() => {
          if (canHover) setStoryOpen(true);
        }} data-id="element-78" data-mp="64hb46d9zdxt">
            <div className="rounded-2xl border border-gold-400/25 bg-[#24354A]/60 p-6 md:p-7" data-id="element-79" data-mp="3nh1jsmj1fk46">
              <h2 className="font-serif text-2xl md:text-3xl text-gold-400 mb-4" data-id="element-80" data-mp="3jfgb7ddjdrzs">
                {copy.coachHeading}
              </h2>

              <p className="font-serif italic text-cream-50/75 text-base leading-relaxed" data-id="element-81" data-mp="39n0tmh65j5ff">
                {copy.expectations}
              </p>
              {copy.intro.map((paragraph, index) => <p key={index} className={`text-cream-50/90 text-base leading-relaxed ${index === 0 ? 'mt-5' : 'mt-4'}`} data-id="element-82" data-mp="2zpw3y7d3krtv" data-mpi={index}>
                  {paragraph}
                </p>)}

              {/* Expanded story */}
              <AnimatePresence initial={false} data-id="element-83" data-mp="se5wffvlfs21">
                {storyOpen && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.4,
                ease: 'easeOut'
              }} className="overflow-hidden" data-id="element-84" data-mp="sc376y29m2yd">
                    <div className="pt-5 mt-5 border-t border-gold-400/20 space-y-4" data-id="element-85" data-mp="9ftq01w2gwa5">
                      {copy.fullStory.map((paragraph, index) => <p key={index} className="text-cream-50/90 text-base leading-relaxed" data-id="element-86" data-mp="3qtp0wdfmm25i" data-mpi={index}>
                          {paragraph}
                        </p>)}

                      <p className="font-serif text-cream-50 text-base pt-1" data-id="element-87" data-mp="3lvniluq0y6v3">
                        {copy.recognizable}{' '}
                        <CalButton variant="text" tone="dark" data-id="element-88" data-mp="338oeajjpv9bf">
                          {copy.recognizableLink}
                        </CalButton>
                      </p>

                      <a href="#about" onClick={event => {
                    event.preventDefault();
                    document.getElementById('about')?.scrollIntoView({
                      behavior: 'smooth'
                    });
                  }} className="inline-block text-gold-400 hover:text-gold-300 underline underline-offset-2 transition-colors text-base" data-id="element-89" data-mp="3c9w1a5gs8nq7">
                        {copy.aboutLink}
                      </a>
                    </div>
                  </motion.div>}
              </AnimatePresence>

              <button type="button" onClick={() => setStoryOpen(open => !open)} aria-expanded={storyOpen} className="mt-5 inline-block text-xs uppercase tracking-widest text-gold-400 hover:text-gold-300 underline underline-offset-4 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-400/60 rounded" data-id="element-90" data-mp="3tt26o3qk7dh">
                {storyOpen ? copy.storyLinkOpen : copy.storyLink}
              </button>
            </div>
          </motion.div>
        </div>

        {/* Buttons — centered below */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 1.2,
        duration: 0.8
      }} className="relative z-20 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5" data-id="element-91" data-mp="35pci2hcs88nb">
          <CalButton tone="dark" data-id="element-92" data-mp="2vu2gtvu7k5al">{copy.primaryCta}</CalButton>
          <Button variant="heroOutline" href="#offerings" data-id="element-93" data-mp="1pd750q8rql6s">
            {copy.secondaryCta}
          </Button>
        </motion.div>
      </div>

    </section>;
}